import { useEffect, useRef } from "react";
import { useReducedMotion } from "framer-motion";

function readCanvasTheme() {
  const s = getComputedStyle(document.documentElement);
  const scale = Number(s.getPropertyValue("--canvas-opacity-scale").trim()) || 1;
  return {
    scale,
    auroraA: s.getPropertyValue("--canvas-aurora-a").trim() || "0, 245, 212",
    auroraB: s.getPropertyValue("--canvas-aurora-b").trim() || "6, 182, 212",
    line: s.getPropertyValue("--canvas-line").trim() || "0, 245, 212",
    lineAlt: s.getPropertyValue("--canvas-line-alt").trim() || "6, 182, 212",
    node: s.getPropertyValue("--canvas-node").trim() || "148, 197, 210",
  };
}

export default function DataCanvas({ className = "", density = 1 }) {
  const ref = useRef(null);
  const reduced = useReducedMotion();

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");
    let w = 0;
    let h = 0;
    let raf;
    let visible = true;
    let theme = readCanvasTheme();
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      w = rect.width;
      h = rect.height;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    const nodeCount = Math.round(34 * density);
    const nodes = Array.from({ length: nodeCount }, () => ({
      x: Math.random(),
      y: Math.random(),
      vx: (Math.random() - 0.5) * 0.00016,
      vy: (Math.random() - 0.5) * 0.00016,
      r: 0.8 + Math.random() * 1.4,
    }));

    const paths = Array.from({ length: 3 }, (_, i) => ({
      t: Math.random(),
      speed: 0.0011 + i * 0.0004,
      y0: 0.22 + i * 0.26,
      amp: 0.06 + Math.random() * 0.05,
    }));

    const aurora = [
      { x: 0.24, y: 0.3, r: 0.5, key: "auroraA", phase: 0 },
      { x: 0.78, y: 0.62, r: 0.55, key: "auroraB", phase: 2.2 },
    ];

    const pathPoint = (p, tt) => {
      const x = tt;
      const y = p.y0 + Math.sin(tt * Math.PI * 2 + p.y0 * 8) * p.amp;
      return [x * w, y * h];
    };

    let time = 0;
    const draw = () => {
      time += 1;
      ctx.clearRect(0, 0, w, h);
      const sc = theme.scale;

      aurora.forEach((a) => {
        const hue = theme[a.key];
        const ax = (a.x + Math.sin(time * 0.0016 + a.phase) * 0.05) * w;
        const ay = (a.y + Math.cos(time * 0.0012 + a.phase) * 0.04) * h;
        const rad = a.r * Math.max(w, h) * 0.6;
        const g = ctx.createRadialGradient(ax, ay, 0, ax, ay, rad);
        g.addColorStop(0, `rgba(${hue}, ${0.055 * sc})`);
        g.addColorStop(1, "rgba(0,0,0,0)");
        ctx.fillStyle = g;
        ctx.fillRect(0, 0, w, h);
      });

      for (let k = 0; k < 3; k += 1) {
        ctx.beginPath();
        ctx.ellipse(
          w * 0.68,
          h * 0.46,
          140 + k * 80 + Math.sin(time * 0.002 + k) * 7,
          86 + k * 52 + Math.cos(time * 0.0016 + k) * 5,
          time * 0.00035 + k * 0.45,
          0,
          Math.PI * 2
        );
        ctx.strokeStyle = `rgba(${theme.line}, ${(0.055 - k * 0.014) * sc})`;
        ctx.lineWidth = 1;
        ctx.stroke();
      }

      paths.forEach((p) => {
        ctx.beginPath();
        for (let tt = 0; tt <= 1.001; tt += 0.02) {
          const [px, py] = pathPoint(p, tt);
          if (tt === 0) ctx.moveTo(px, py);
          else ctx.lineTo(px, py);
        }
        ctx.strokeStyle = `rgba(${theme.lineAlt}, ${0.1 * sc})`;
        ctx.lineWidth = 1;
        ctx.stroke();

        p.t = (p.t + p.speed) % 1;
        const [dx, dy] = pathPoint(p, p.t);
        ctx.beginPath();
        ctx.arc(dx, dy, 2, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${theme.line}, ${0.75 * Math.min(1, sc + 0.25)})`;
        ctx.shadowColor = `rgba(${theme.line}, ${0.55 * sc})`;
        ctx.shadowBlur = sc < 0.5 ? 4 : 10;
        ctx.fill();
        ctx.shadowBlur = 0;
      });

      nodes.forEach((n) => {
        n.x += n.vx;
        n.y += n.vy;
        if (n.x < 0 || n.x > 1) n.vx *= -1;
        if (n.y < 0 || n.y > 1) n.vy *= -1;
      });

      for (let i = 0; i < nodes.length; i += 1) {
        for (let j = i + 1; j < nodes.length; j += 1) {
          const a = nodes[i];
          const b = nodes[j];
          const dx = (a.x - b.x) * w;
          const dy = (a.y - b.y) * h;
          const d2 = dx * dx + dy * dy;
          const max = 130;
          if (d2 < max * max) {
            const alpha = (1 - Math.sqrt(d2) / max) * 0.12 * sc;
            ctx.beginPath();
            ctx.moveTo(a.x * w, a.y * h);
            ctx.lineTo(b.x * w, b.y * h);
            ctx.strokeStyle = `rgba(${theme.line}, ${alpha})`;
            ctx.lineWidth = 0.7;
            ctx.stroke();
          }
        }
      }

      nodes.forEach((n) => {
        ctx.beginPath();
        ctx.arc(n.x * w, n.y * h, n.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${theme.node}, ${0.4 * Math.min(1, sc + 0.15)})`;
        ctx.fill();
      });
    };

    const loop = () => {
      if (!visible) {
        raf = undefined;
        return;
      }
      draw();
      raf = requestAnimationFrame(loop);
    };

    const startLoop = () => {
      if (reduced || raf != null) return;
      raf = requestAnimationFrame(loop);
    };

    const stopLoop = () => {
      if (raf != null) {
        cancelAnimationFrame(raf);
        raf = undefined;
      }
    };

    const io = new IntersectionObserver(
      ([entry]) => {
        visible = entry.isIntersecting;
        if (visible) {
          if (reduced) draw();
          else startLoop();
        } else {
          stopLoop();
        }
      },
      { rootMargin: "80px" }
    );
    io.observe(canvas);

    if (reduced) {
      draw();
    } else {
      startLoop();
    }

    return () => {
      stopLoop();
      io.disconnect();
      ro.disconnect();
    };
  }, [density, reduced]);

  return <canvas ref={ref} className={className} aria-hidden="true" />;
}
